import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useKeepFocusedVisible, alignFieldToVisible } from '@/shared/hooks/use-keep-focused-visible';
import { useKeyboardScreenStyle } from '@/shared/hooks/use-visual-viewport';
import { QuizBackButton } from './quiz-back-button';
import { CompanyLogo } from './company-logo';
import { useClaimsStore } from '../store';
import {
  CITY_MAX_LENGTH,
  getProfileErrors,
  PERSON_NAME_MAX_LENGTH,
  PROFILE_FIELD_ORDER,
  US_ZIP_MAX_LENGTH,
  type ProfileFieldId,
} from '../domain/profile';
import { upToAmountLabel } from '../domain/payout';
import { US_STATES } from '../domain/us-states';
import { useQuizStore } from '@/stores/quiz-store';
import emailIcon from '../assets/icon-email.svg';

interface ProfileStepProps {
  onBack: () => void;
  onAdvance: () => void;
}

export function ProfileStep({ onBack, onAdvance }: ProfileStepProps) {
  const selected = useClaimsStore((s) => s.selected);
  const removeSelected = useClaimsStore((s) => s.removeSelected);
  const profile = useClaimsStore((s) => s.profile);
  const setProfile = useClaimsStore((s) => s.setProfile);
  const quizEmail = useQuizStore((s) => s.answers.email);
  const [attempted, setAttempted] = useState(false);
  const fieldRefs = useRef<Partial<Record<ProfileFieldId, HTMLElement | null>>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const keyboardStyle = useKeyboardScreenStyle();
  const { spacerPx } = useKeepFocusedVisible(scrollRef, 96);

  useEffect(() => {
    if (!profile.email.trim() && typeof quizEmail === 'string' && quizEmail) {
      setProfile({ email: quizEmail });
    }
  }, [profile.email, quizEmail, setProfile]);

  const errors = attempted ? getProfileErrors(profile) : {};

  function bindField(id: ProfileFieldId) {
    return (el: HTMLElement | null) => {
      fieldRefs.current[id] = el;
    };
  }

  function scrollFieldIntoView(id: ProfileFieldId, opts?: { focus?: boolean }) {
    const container = scrollRef.current;
    const node = fieldRefs.current[id];
    if (!container || !node) return;
    alignFieldToVisible(container, node, 96);
    if (opts?.focus === false) return;
    const focusable = node.querySelector('input, button');
    if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
  }

  function handleContinue() {
    const next = getProfileErrors(profile);
    const first = PROFILE_FIELD_ORDER.find((id) => next[id]);
    if (!first && selected.length > 0) {
      onAdvance();
      return;
    }
    setAttempted(true);
    if (!first) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollFieldIntoView(first, { focus: false }));
    });
  }

  return (
    <div
      className="flex h-[100dvh] flex-col overflow-hidden bg-mf-neutral-50 font-body text-mf-neutral-900"
      style={keyboardStyle}
    >
      <div className="relative min-h-0 flex-1">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 px-4 py-3">
          <QuizBackButton onClick={onBack} className="pointer-events-auto" />
        </div>
        <div ref={scrollRef} className="h-full overflow-y-auto overflow-anchor-none overscroll-y-contain">
          <div className="flex flex-col gap-6 px-4 pb-16 pt-3">
        <h1 className="flex h-9 items-center justify-center px-12 text-center text-lg font-bold leading-6 text-mf-neutral-900">
          Fill out your details
        </h1>
        <div className="rounded-[20px] bg-white shadow-[0_0_8px_rgba(26,22,19,0.03)]">
          {selected.length === 0 && (
            <p className="px-4 py-4 text-sm text-mf-neutral-600">
              No claims selected. Go back to add at least one.
            </p>
          )}
          {selected.map((item, index) => (
            <div key={item.id}>
              {index > 0 && (
                <div className="pl-[60px] pr-4">
                  <div className="h-px bg-mf-neutral-100" />
                </div>
              )}
              <div className="flex items-center gap-2 px-4 py-3">
                <CompanyLogo name={item.company} src={item.iconUrl} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium leading-4 text-mf-neutral-900">
                    {item.company}
                  </p>
                  <p className="text-[11px] leading-[14px] text-mf-neutral-400">
                    {upToAmountLabel(item)}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${item.company}`}
                  onClick={() => removeSelected(item.id)}
                  className="flex size-5 items-center justify-center text-mf-neutral-400 hover:text-mf-neutral-900"
                >
                  <X className="size-5" strokeWidth={1.75} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-bold leading-6">Contact information</h2>
          <Field
            label="First name*"
            value={profile.first_name}
            onChange={(v) => setProfile({ first_name: v })}
            placeholder="First Name"
            autoComplete="given-name"
            maxLength={PERSON_NAME_MAX_LENGTH}
            error={errors.first_name}
            fieldRef={bindField('first_name')}
          />
          <Field
            label="Middle name (optional)"
            value={profile.middle_name}
            onChange={(v) => setProfile({ middle_name: v })}
            placeholder="Middle name (optional)"
            autoComplete="additional-name"
            maxLength={PERSON_NAME_MAX_LENGTH}
            error={errors.middle_name}
            fieldRef={bindField('middle_name')}
          />
          <Field
            label="Last name*"
            value={profile.last_name}
            onChange={(v) => setProfile({ last_name: v })}
            placeholder="Last Name"
            autoComplete="family-name"
            maxLength={PERSON_NAME_MAX_LENGTH}
            error={errors.last_name}
            fieldRef={bindField('last_name')}
          />
          <Field
            label="Street address*"
            value={profile.street_address}
            onChange={(v) => setProfile({ street_address: v })}
            placeholder="Street address"
            autoComplete="street-address"
            error={errors.street_address}
            fieldRef={bindField('street_address')}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs leading-4 text-mf-neutral-600">Country*</label>
            <div className="flex h-[52px] items-center gap-2 rounded-xl bg-white px-4 shadow-[0_0_16px_rgba(26,22,19,0.03)]">
              <span className="font-display text-xl font-bold leading-6">🇺🇸</span>
              <span className="flex-1 text-base leading-5">United States</span>
            </div>
          </div>
          <Field
            label="City*"
            value={profile.city}
            onChange={(v) => setProfile({ city: v })}
            placeholder="City"
            autoComplete="address-level2"
            maxLength={CITY_MAX_LENGTH}
            error={errors.city}
            fieldRef={bindField('city')}
          />
          <StateSelect
            value={profile.state}
            onChange={(v) => setProfile({ state: v })}
            error={errors.state}
            fieldRef={bindField('state')}
          />
          <Field
            label="ZIP / Postal Code*"
            value={profile.postal_code}
            onChange={(v) => setProfile({ postal_code: v })}
            placeholder="ZIP / Postal Code"
            autoComplete="postal-code"
            inputMode="text"
            maxLength={US_ZIP_MAX_LENGTH}
            error={errors.postal_code}
            fieldRef={bindField('postal_code')}
          />
          <Field
            label="Email Address*"
            value={profile.email}
            onChange={(v) => setProfile({ email: v })}
            placeholder="Email Address"
            autoComplete="email"
            type="email"
            inputMode="email"
            error={errors.email}
            fieldRef={bindField('email')}
            icon={emailIcon}
          />
        </div>
            <div aria-hidden className="shrink-0" style={{ height: spacerPx }} />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-transparent to-mf-neutral-50" />
      </div>

      <div className="shrink-0 bg-white px-4 py-3">
        <button
          type="button"
          onClick={handleContinue}
          className="flex h-[52px] w-full items-center justify-center rounded-full bg-mf-neutral-900 px-5 text-base font-bold text-white"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  fieldRef,
  icon,
  ...inputProps
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  fieldRef?: (el: HTMLElement | null) => void;
  icon?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <label ref={fieldRef} data-keyboard-anchor className="flex w-full flex-col gap-1">
      <span className="text-xs leading-4 text-mf-neutral-600">{label}</span>
      <div
        className={cn(
          'flex h-[52px] items-center gap-2 rounded-xl bg-white px-4 shadow-[0_0_16px_rgba(26,22,19,0.03)]',
          error && 'border-[1.5px] border-mf-error',
        )}
      >
        {icon ? (
          <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden">
            <img src={icon} alt="" className="h-[18px] w-[22px]" />
          </span>
        ) : null}
        <input
          {...inputProps}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          className="h-full min-w-0 flex-1 bg-transparent text-base leading-5 text-mf-neutral-900 outline-none placeholder:text-[#c0b7ae]"
        />
      </div>
      {error ? <p className="text-xs leading-4 text-[#e53e3e]">{error}</p> : null}
    </label>
  );
}

function StateSelect({
  value,
  onChange,
  error,
  fieldRef,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  fieldRef?: (el: HTMLElement | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = US_STATES.find((s) => s.name === value || s.code === value);

  return (
    <div ref={fieldRef} data-keyboard-anchor className="flex flex-col gap-1">
      <span className="text-xs leading-4 text-mf-neutral-600">State*</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-invalid={error ? true : undefined}
          className={cn(
            'flex h-[52px] w-full items-center gap-2 rounded-xl bg-white px-4 text-left shadow-[0_0_16px_rgba(26,22,19,0.03)]',
            error && 'border-[1.5px] border-mf-error',
          )}
        >
          <span
            className={cn(
              'flex-1 text-base leading-5',
              selected ? 'text-mf-neutral-900' : 'text-[#c0b7ae]',
            )}
          >
            {selected?.name ?? 'Select State'}
          </span>
          <ChevronDown className="size-6 text-mf-neutral-900" strokeWidth={1.75} />
        </button>
        {open && (
          <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl bg-white py-1 shadow-[0_0_16px_rgba(26,22,19,0.12)]">
            {US_STATES.map((state) => (
              <li key={state.code}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(state.name);
                    setOpen(false);
                  }}
                  className="flex h-10 w-full items-center px-4 text-left text-sm text-mf-neutral-900 hover:bg-mf-neutral-50"
                >
                  {state.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error ? <p className="text-xs leading-4 text-[#e53e3e]">{error}</p> : null}
    </div>
  );
}
